import { useRef, useCallback, useState } from "react";

// Swipe-right-to-go-back gesture handler for sub-views inside a tool.
//
// Two modes:
//   - Default (legacy): commit-only. Returns { onTouchStart, onTouchMove,
//     onTouchEnd }. Fires onBack() once when the user drags >30% of screen
//     width or flicks fast (≥0.4 px/ms with at least 40px travel). No
//     visual feedback during the drag.
//   - iOS-native (opts.edgeOnly = true): the gesture starts ONLY from the
//     screen's left edge (within edgePx, default 25px) and the hook
//     additionally exposes a live `dx` (clamped) plus `dragging` so the
//     consumer can transform its current view to follow the finger.
//     Mirrors iPhone's interactive back gesture — view tracks the drag,
//     springs back if released early, completes if released past commit.
//
// App.js has a top-level swipe-back that exits the current tool. This
// hook calls stopPropagation() on touchstart so App's outer handler never
// registers the start and skips all subsequent move/end events.
//
// Usage (commit-only, default):
//   const swipe = useSwipeBack(onBack);
//   <div onTouchStart={swipe.onTouchStart} onTouchMove={swipe.onTouchMove} onTouchEnd={swipe.onTouchEnd}>
//
// Usage (iOS-native with live transform):
//   const { onTouchStart, onTouchMove, onTouchEnd, dx, dragging } = useSwipeBack(onBack, { edgeOnly: true });
//   <div
//     onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}
//     style={{ transform: `translateX(${dx}px)`, transition: dragging ? 'none' : 'transform 0.3s cubic-bezier(0.22,0.61,0.36,1)' }}
//   >
export default function useSwipeBack(onBack, opts = {}) {
  const { edgeOnly = false, edgePx = 25, commitFraction = 0.30, commitVelocity = 0.4 } = opts;

  const startX = useRef(null);
  const startY = useRef(null);
  const startT = useRef(null);
  const moving = useRef(false);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);

  const onTouchStart = useCallback((e) => {
    const x = e.touches[0].clientX;
    // Edge-only mode rejects touches that don't start at the screen's
    // left edge — same constraint iOS uses for its interactive back.
    if (edgeOnly && x > edgePx) return;
    // Stop the outer App.js onBackSwipeStart from registering — it won't
    // then fire move/end because its start ref stays null.
    e.stopPropagation();
    startX.current = x;
    startY.current = e.touches[0].clientY;
    startT.current = Date.now();
    moving.current = false;
    setDx(0);
  }, [edgeOnly, edgePx]);

  const onTouchMove = useCallback((e) => {
    if (startX.current === null) return;
    e.stopPropagation();
    const rawDx = e.touches[0].clientX - startX.current;
    const dy = Math.abs(e.touches[0].clientY - startY.current);
    if (!moving.current) {
      if (dy > 8) { startX.current = null; setDx(0); setDragging(false); return; } // vertical scroll — cancel
      if (rawDx < 6) return;
      moving.current = true;
      setDragging(true);
    }
    // Live track for the consumer's transform. Apply iOS-style mild
    // resistance past 80% of screen width so the view doesn't fly off
    // before commit. Right-only drag (clamp negatives to 0).
    const clamped = Math.max(0, rawDx);
    const w = window.innerWidth || 400;
    const eased = clamped < w * 0.8 ? clamped : (w * 0.8) + (clamped - w * 0.8) * 0.4;
    setDx(eased);
  }, []);

  const onTouchEnd = useCallback((e) => {
    if (startX.current === null || !moving.current) {
      startX.current = null;
      moving.current = false;
      setDragging(false);
      setDx(0);
      return;
    }
    e.stopPropagation();
    const rawDx = e.changedTouches[0].clientX - startX.current;
    const dt = Date.now() - startT.current;
    const velocity = rawDx / Math.max(1, dt);
    startX.current = null;
    moving.current = false;
    const w = window.innerWidth || 400;
    const commit = rawDx > w * commitFraction || (velocity >= commitVelocity && rawDx > 40);
    setDragging(false);
    if (commit && typeof onBack === "function") {
      // Animate the view fully off-screen before firing onBack so the
      // transition reads as a complete slide-out, matching iOS.
      setDx(w);
      setTimeout(() => {
        setDx(0);
        onBack();
      }, 200);
    } else {
      // Spring back to origin.
      setDx(0);
    }
  }, [onBack, commitFraction, commitVelocity]);

  return { onTouchStart, onTouchMove, onTouchEnd, dx, dragging };
}
